import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import pluralize from 'pluralize';
import cx from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiMenu from '@material-ui/core/Menu';
import MuiMenuItem from '@material-ui/core/MenuItem';
import {
  Business as InPersonVisitIcon,
  ContactMail as DirectMailIcon,
} from '@material-ui/icons';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import EmailIcon from '@material-ui/icons/Email';
import CallIcon from '@material-ui/icons/Call';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import AssignmentIcon from '@material-ui/icons/Assignment';
import LocalPizzaIcon from '@material-ui/icons/LocalPizza';
import LinkedInIcon from 'Modules/Shared/icons/linkedIn';
import SendosoIcon from 'Modules/Shared/icons/sendoso';
import Avatar from '@material-ui/core/Avatar';
import { Link } from 'Utils/history';
import { STEP_TYPES } from 'Modules/App/constants';
import { flowActionLinkClicked } from 'Modules/Home/actions';

const styles = theme => {
  return {
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    text: {
      color: theme.palette.text.secondary,
    },
    stepsDueDropdownButton: {
      width: 'auto',
      flex: 1,
      marginLeft: theme.spacing.unit * 12,
    },
    stepsDropdown: {
      padding: 0,
    },
    stepMenuItem: {
      height: 8,
    },
    workStepLink: {
      display: 'flex',
      alignItems: 'center',
    },
    avatar: {
      marginRight: 10,
      height: 24,
      width: 24,
    },
    avatarIcon: {
      width: 16,
      height: 16,
      color: theme.palette.text.neutralColor,
      fill: theme.palette.text.neutralColor,
    },
    avatarPrimary: {
      backgroundColor: theme.palette.primary.main,
    },
  };
};

@withStyles(styles)
@connect(
  () => {
    return {};
  },
  {
    flowActionLinkClicked,
  }
)
export default class StepsDueDropdown extends Component {
  static displayName = 'StepsDueDropdown';
  static propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    styleOverrides: PropTypes.objectOf(
      PropTypes.objectOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      )
    ),
    steps: PropTypes.instanceOf(List).isRequired,
    flowActionLinkClicked: PropTypes.func.isRequired,
  };

  static defaultProps = {
    styleOverrides: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      anchorEl: null,
    };
  }

  trackFlowActionLink = () => {
    const { flowActionLinkClicked } = this.props;
    flowActionLinkClicked({ action: 'Step' });
  };

  buildStepIcon(stepType) {
    const { classes } = this.props;

    let formattedType = stepType.toLowerCase();
    if (stepType.includes('STEP_SUBTYPE')) {
      formattedType = stepType.match(/STEP_SUBTYPE_(.*)/)[1].toLowerCase();
    }

    switch (formattedType) {
      case 'template':
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <EmailIcon className={classes.avatarIcon} />
          </Avatar>
        );
      case 'call':
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <CallIcon className={classes.avatarIcon} />
          </Avatar>
        );
      case 'sms':
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <ChatBubbleIcon className={classes.avatarIcon} />
          </Avatar>
        );
      case STEP_TYPES.DIRECT_MAIL:
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <DirectMailIcon className={classes.avatarIcon} />
          </Avatar>
        );
      case STEP_TYPES.IN_PERSON_VISIT:
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <InPersonVisitIcon className={classes.avatarIcon} />
          </Avatar>
        );
      case STEP_TYPES.LINKEDIN_CONNECT:
      case STEP_TYPES.LINKEDIN_INMAIL:
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <LinkedInIcon className={classes.avatarIcon} />
          </Avatar>
        );
      case STEP_TYPES.SENDOSO:
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <SendosoIcon className={classes.avatarIcon} />
          </Avatar>
        );
      case 'other':
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <AssignmentIcon className={classes.avatarIcon} />
          </Avatar>
        );
      default:
        return (
          <Avatar className={cx(classes.avatar, classes.avatarPrimary)}>
            <LocalPizzaIcon className={classes.avatarIcon} />
          </Avatar>
        );
    }
  }

  render() {
    const { classes, steps, styleOverrides } = this.props;

    return (
      <div className={classes.root}>
        <MuiIconButton
          classes={{ root: classes.stepsDueDropdownButton }}
          onClick={event => {
            this.setState({
              isOpen: true,
              anchorEl: event.currentTarget,
            });
          }}
        >
          <Typography
            className={classes.text}
            variant="caption"
            noWrap
            style={styleOverrides.buttonText}
          >
            {`${steps.size} ${pluralize('step', steps.size)} due`}
          </Typography>
          <ArrowDropDownIcon />
        </MuiIconButton>
        <MuiMenu
          className={classes.stepsDropdown}
          open={this.state.isOpen}
          onClose={() => {
            this.setState({ isOpen: false });
          }}
          anchorEl={this.state.anchorEl}
        >
          {steps
            .map(step => {
              return (
                <MuiMenuItem
                  key={step.get('id')}
                  className={classes.stepMenuItem}
                  onClick={this.trackFlowActionLink}
                >
                  <Link
                    to={`/steps/${step.get('id')}/work`}
                    className={classes.workStepLink}
                  >
                    {this.buildStepIcon(step.get('stepType'))}
                    <Typography variant="body1">{step.get('name')}</Typography>
                  </Link>
                </MuiMenuItem>
              );
            })
            .valueSeq()}
        </MuiMenu>
      </div>
    );
  }
}
